import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PasswordInput from '../../components/ui/PasswordInput';
import { useAuth } from '../../context/AuthContext';
import AuthShell from './AuthShell';

const ROLE_OPTIONS = [
  {
    value: 'fpo_admin',
    title: 'I run / manage an FPO',
    blurb: 'List aggregated produce, manage member farmers, accept orders.',
  },
  {
    value: 'buyer',
    title: 'I buy in bulk',
    blurb: 'Browse FPO listings, place orders, track deliveries.',
  },
];

function extractErrorMessage(err) {
  const data = err?.response?.data;
  if (!data) return 'Something went wrong. Please try again.';
  if (typeof data === 'string') return data;
  if (data.non_field_errors?.length) return data.non_field_errors[0];
  const firstFieldKey = Object.keys(data)[0];
  if (firstFieldKey && Array.isArray(data[firstFieldKey])) {
    return `${firstFieldKey}: ${data[firstFieldKey][0]}`;
  }
  return JSON.stringify(data);
}

export default function Signup() {
  const { user, signup, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: '',
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role: 'fpo_admin',
  });
  const [error, setError] = useState('');

  if (user) {
    return <Navigate to="/app" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signup(form);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <AuthShell
      title="Create your Atharva account"
      subtitle="Pick the role that matches you. You can change it later by contacting support."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-green-700 hover:text-green-800">
            Sign in
          </Link>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-gray-700">I am a…</legend>
          {ROLE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                form.role === opt.value
                  ? 'border-green-600 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="role"
                value={opt.value}
                checked={form.role === opt.value}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 h-4 w-4 accent-green-700"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{opt.title}</span>
                <span className="block text-xs text-gray-600">{opt.blurb}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <Input
          name="full_name"
          label="Full name"
          value={form.full_name}
          onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          required
        />
        <Input
          name="username"
          label="Username"
          autoComplete="username"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          required
        />
        <Input
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />
        <Input
          name="phone"
          label="Phone"
          type="tel"
          autoComplete="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <PasswordInput
          name="password"
          label="Password"
          autoComplete="new-password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
          helpText="At least 8 characters."
        />

        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button type="submit" disabled={loading}>
          {loading ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthShell>
  );
}
